<template>
	<v-card>
		<v-toolbar flat color="dark" dark>
			<v-toolbar-title>
				<span class="pl-2">Aanmeldingen beheren — toets herkansing</span>
			</v-toolbar-title>
		</v-toolbar>

		<v-alert v-if="!examId" type="info" dense outlined class="ma-4">
			Geen toets geselecteerd. Ga terug naar het overzicht en open deze pagina via
			<strong>Aanmeldingen</strong>, of gebruik
			<code>?exam_id=…</code> in de URL.
		</v-alert>

		<v-alert v-else-if="pageError" type="warning" dense outlined class="ma-4">
			{{ pageError }}
		</v-alert>

		<v-card-text v-if="examId && !pageError">
			<v-progress-linear v-if="pageLoading" indeterminate color="primary" class="mb-4"></v-progress-linear>

			<template v-else>
				<v-alert v-if="examLocked" type="info" dense outlined class="mb-4">
					Deze toets is vergrendeld. Studenten kunnen niet worden toegevoegd.
				</v-alert>

				<v-alert v-if="userIdMissing" type="warning" dense outlined class="mb-4">
					Geen geldige gebruiker in de sessie (user id). Log opnieuw in; anders kan niet worden
					bijgehouden wie de aanmelding heeft gedaan.
				</v-alert>

				<p class="subtitle-1 font-weight-medium mb-2">Geselecteerde toets (alleen lezen)</p>
				<v-row v-if="exam" dense>
					<v-col v-for="row in summaryFields" :key="row.label" cols="12" sm="6" md="4">
						<v-text-field
							:value="row.value"
							:label="row.label"
							disabled
							filled
							dense
							hide-details
						></v-text-field>
					</v-col>
				</v-row>

				<v-divider class="my-6"></v-divider>

				<v-alert v-if="!canSelectStudents" type="warning" dense outlined class="mb-4">
					Deze toets heeft geen geldige <strong>klas</strong> en/of <strong>schooljaar</strong>. Alleen
					studenten uit de juiste klas in hetzelfde schooljaar kunnen worden aangemeld.
				</v-alert>

				<v-alert v-if="canSelectStudents && studentsError" type="warning" dense outlined class="mb-4">
					{{ studentsError }}
				</v-alert>

				<v-form v-if="canSelectStudents" @submit.prevent="submit">
					<v-row>
						<v-col cols="12">
							<v-autocomplete
								v-model="selectedStudentIds"
								:items="selectableStudents"
								item-text="text"
								item-value="value"
								label="Studenten voor deze toets"
								hint="Al aangemelde studenten staan vooraf geselecteerd. Verwijder een chip om af te melden; voeg namen toe om aan te melden."
								persistent-hint
								filled
								dense
								multiple
								chips
								deletable-chips
								small-chips
								hide-details="auto"
								:loading="studentsLoading"
								:disabled="examLocked || saving"
								:no-data-text="
									studentsLoading
										? 'Laden…'
										: 'Geen studenten gevonden voor deze klas/schooljaar.'
								"
							></v-autocomplete>
						</v-col>
						<v-col cols="12">
							<v-textarea
								v-model="description"
								label="Omschrijving (optioneel, geldt voor alle geselecteerde studenten)"
								filled
								dense
								rows="3"
								:disabled="examLocked || saving"
								hide-details="auto"
							></v-textarea>
						</v-col>
					</v-row>

					<v-alert v-if="submitError" type="error" dense outlined class="mt-2">
						{{ submitError }}
					</v-alert>

					<v-card-actions class="px-0 pt-4">
						<v-btn
							color="primary"
							depressed
							:disabled="examLocked || saving || !hasChanges || (needsUserForSave && userIdMissing)"
							:loading="saving"
							type="submit"
						>
							Opslaan
						</v-btn>
						<v-btn color="dark" depressed :disabled="saving" @click="goBack">Annuleren</v-btn>
					</v-card-actions>
				</v-form>
			</template>
		</v-card-text>
	</v-card>
</template>

<script>
import Axios from "axios";
import { API_ROOT } from "../../config";

const ax = Axios.create({
	baseURL: API_ROOT["osbod_salary"],
	timeout: 60000
});

export default {
	name: "ExamAanmeldingenAddEdit",
	data() {
		return {
			examId: null,
			exam: null,
			pageLoading: false,
			pageError: "",
			studentsLoading: false,
			studentsError: "",
			eligibleRows: [],
			registrationRecords: [],
			registrationIdByStudentId: {},
			baselineRegisteredStudentIds: [],
			selectedStudentIds: [],
			description: "",
			saving: false,
			submitError: "",
			user: JSON.parse(localStorage.getItem("user") || "{}")
		};
	},
	computed: {
		loggedInUserId() {
			var u = this.user || {};
			if (u.id != null && u.id !== "") return Number(u.id);
			if (u.user_id != null && u.user_id !== "") return Number(u.user_id);
			return null;
		},
		userIdMissing() {
			if (!this.examId) return false;
			var id = this.loggedInUserId;
			return id == null || isNaN(id);
		},
		examLocked() {
			if (!this.exam || this.exam.locked == null) return false;
			return Number(this.exam.locked) === 1 || this.exam.locked === true || this.exam.locked === "1";
		},
		examClassId() {
			if (!this.exam) return null;
			var v = this.exam.class_id;
			return v != null && v !== "" ? v : null;
		},
		examSchoolyearId() {
			if (!this.exam) return null;
			var v = this.exam.schoolyear_id;
			return v != null && v !== "" ? v : null;
		},
		canSelectStudents() {
			return this.examClassId != null && this.examSchoolyearId != null;
		},
		summaryFields() {
			if (!this.exam) return [];
			var pick = this.pickExamField.bind(this);
			var d = this.exam;
			return [
				{
					label: "Toets-ID",
					value: pick(d, ["id", "exam_id", "tbl_exam.id"])
				},
				{
					label: "Examen",
					value: pick(d, [
						"tbl_sys_exam_type.name",
						"tbl_exam_type.name",
						"exam_type.name",
						"tbl_sys_exam_type_name",
						"exam_type_name",
						"examen"
					])
				},
				{
					label: "Sector",
					value: pick(d, [
						"tbl_sys_sector.name",
						"sector.name",
						"tbl_sys_sector_name",
						"sector_name"
					])
				},
				{
					label: "Opleiding",
					value: pick(d, [
						"tbl_sys_study.name",
						"study.name",
						"tbl_sys_study_name",
						"study_name",
						"opleiding"
					])
				},
				{
					label: "Klas",
					value: pick(d, [
						"tbl_sys_class.name",
						"class.name",
						"tbl_sys_class_name",
						"class_name",
						"klas"
					])
				},
				{
					label: "Periode",
					value: pick(d, [
						"tbl_sys_periode.name",
						"periode.name",
						"tbl_sys_periode_name",
						"periode_name"
					])
				},
				{
					label: "Deelkwalificatie",
					value: pick(d, [
						"tbl_sys_subject.name",
						"subject.name",
						"tbl_sys_subject_name",
						"subject_name",
						"deelkwalificatie"
					])
				},
				{
					label: "Module",
					value: pick(d, [
						"tbl_teacher_subject_class.module",
						"teacher_subject_class.module",
						"module",
						"module_name"
					])
				},
				{
					label: "Docent",
					value: pick(d, [
						"tbl_employee.teacher_name",
						"employee.teacher_name",
						"tbl_employee.display_name",
						"teacher_name",
						"docent",
						"docent_naam"
					])
				}
			];
		},
		selectableStudents() {
			var seen = new Set();
			var out = [];
			function pushStudent(st, sid) {
				if (!st || st.id == null) return;
				sid = Number(sid);
				if (isNaN(sid) || seen.has(sid)) return;
				seen.add(sid);
				var text =
					st.display_name ||
					[String(st.firstname || "").trim(), String(st.surname || "").trim()].filter(Boolean).join(" ") ||
					"Student #" + sid;
				out.push({ text: text, value: sid });
			}
			var i;
			for (i = 0; i < this.eligibleRows.length; i++) {
				var row = this.eligibleRows[i];
				pushStudent(row.tbl_student, row.tbl_student && row.tbl_student.id);
			}
			for (i = 0; i < this.registrationRecords.length; i++) {
				var r = this.registrationRecords[i];
				var st = r.tbl_student;
				var sid = r.student_id != null ? r.student_id : st && st.id;
				pushStudent(st, sid);
			}
			out.sort(function (a, b) {
				return String(a.text).localeCompare(String(b.text), "nl", { sensitivity: "base" });
			});
			return out;
		},
		hasChanges() {
			var norm = function (arr) {
				return arr
					.map(function (x) {
						return Number(x);
					})
					.filter(function (n) {
						return !isNaN(n);
					})
					.sort(function (a, b) {
						return a - b;
					})
					.join(",");
			};
			return norm(this.selectedStudentIds) !== norm(this.baselineRegisteredStudentIds);
		},
		needsUserForSave() {
			var baseline = new Set(
				this.baselineRegisteredStudentIds.map(function (x) {
					return Number(x);
				})
			);
			var i;
			for (i = 0; i < this.selectedStudentIds.length; i++) {
				if (!baseline.has(Number(this.selectedStudentIds[i]))) return true;
			}
			return false;
		}
	},
	watch: {
		"$route.query.exam_id": function () {
			this.initFromRoute();
		}
	},
	created() {
		this.initFromRoute();
	},
	methods: {
		pickExamDisplay(obj, dotPath) {
			if (obj == null || dotPath == null) return null;
			var parts = String(dotPath).split(".");
			var cur = obj;
			for (var i = 0; i < parts.length; i++) {
				if (cur == null || typeof cur !== "object") return null;
				cur = cur[parts[i]];
			}
			if (cur == null || cur === "") return null;
			return String(cur);
		},
		pickExamField(root, paths) {
			if (!root) return "—";
			var sources = [root];
			if (root.tbl_exam && typeof root.tbl_exam === "object") {
				sources.push(root.tbl_exam);
			}
			for (var s = 0; s < sources.length; s++) {
				for (var p = 0; p < paths.length; p++) {
					var v = this.pickExamDisplay(sources[s], paths[p]);
					if (v != null) return v;
				}
			}
			return "—";
		},
		goBack() {
			if (this.examId) {
				this.$router.push({
					path: "/schooladmin/exam_aanmeldingen",
					query: { exam_id: String(this.examId) }
				});
			} else {
				this.$router.push({ path: "/schooladmin/exam_aanmeldingen" });
			}
		},
		unwrapExamPayload(payload) {
			if (
				payload &&
				payload.data != null &&
				typeof payload.data === "object" &&
				!Array.isArray(payload.data)
			) {
				return payload.data;
			}
			return payload;
		},
		normalizeListPayload(payload) {
			if (payload == null) return [];
			if (Array.isArray(payload)) return payload;
			if (typeof payload === "object") {
				if (Array.isArray(payload.data)) return payload.data;
				if (Array.isArray(payload.records)) return payload.records;
				if (Array.isArray(payload.items)) return payload.items;
			}
			return [];
		},
		/**
		 * Filter GET /student_class/all_active rows to the exam's class + school year; dedupe per student.
		 */
		filterAllActiveForExam(rows, classId, schoolyearId) {
			var cid = Number(classId);
			var sy = Number(schoolyearId);
			var map = new Map();
			var i;
			for (i = 0; i < rows.length; i++) {
				var row = rows[i];
				if (!row || Number(row.class_id) !== cid || Number(row.schoolyear_id) !== sy) continue;
				var st = row.tbl_student;
				if (!st || st.id == null) continue;
				var sid = Number(st.id);
				if (isNaN(sid)) continue;
				if (!map.has(sid)) map.set(sid, row);
			}
			return Array.from(map.values());
		},
		/**
		 * Union: student_class rows + student_overtake_class rows (for_exam) by student id; same tbl_student shape for the picker.
		 */
		mergeOvertakeClassIntoEligible(classRows, overtakeRows) {
			var map = new Map();
			var i;
			for (i = 0; i < classRows.length; i++) {
				var row = classRows[i];
				var st = row.tbl_student;
				if (!st || st.id == null) continue;
				var sid = Number(st.id);
				if (isNaN(sid)) continue;
				if (!map.has(sid)) map.set(sid, row);
			}
			for (i = 0; i < overtakeRows.length; i++) {
				var oc = overtakeRows[i];
				var st2 = oc.tbl_student;
				if (!st2 || st2.id == null) continue;
				var sid2 = Number(st2.id);
				if (isNaN(sid2)) continue;
				if (!map.has(sid2)) map.set(sid2, { tbl_student: st2 });
			}
			return Array.from(map.values());
		},
		initFromRoute() {
			var q = this.$route.query.exam_id;
			this.examId = q != null && String(q).trim() !== "" ? String(q).trim() : null;
			this.exam = null;
			this.pageError = "";
			this.studentsError = "";
			this.eligibleRows = [];
			this.registrationRecords = [];
			this.registrationIdByStudentId = {};
			this.baselineRegisteredStudentIds = [];
			this.selectedStudentIds = [];
			this.description = "";
			this.submitError = "";
			if (!this.examId) {
				return;
			}
			this.loadPage();
		},
		loadPage() {
			var self = this;
			if (!this.examId) return;
			this.pageLoading = true;
			this.pageError = "";
			ax.get("/exam/single/" + this.examId)
				.then(function (response) {
					var payload = self.unwrapExamPayload(response.data);
					if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
						self.pageError = "Toets niet gevonden of ongeldig antwoord.";
						self.exam = null;
						return;
					}
					self.exam = payload;
					if (self.canSelectStudents) {
						self.loadEligibleAndRegistered();
					}
				})
				.catch(function () {
					self.pageError = "Toets kon niet worden geladen.";
					self.exam = null;
				})
				.finally(function () {
					self.pageLoading = false;
				});
		},
		loadEligibleAndRegistered() {
			var self = this;
			this.studentsLoading = true;
			this.studentsError = "";
			this.eligibleRows = [];
			this.registrationRecords = [];
			this.registrationIdByStudentId = {};
			this.baselineRegisteredStudentIds = [];
			this.selectedStudentIds = [];
			var cid = this.examClassId;
			var syid = this.examSchoolyearId;
			Promise.all([
				ax.get("/student_class/all_active"),
				ax.get("/student_overtake_class/for_exam", {
					params: { exam_id: this.examId }
				}),
				ax.get("/student_overtake_exam/all_by_exam", {
					params: { exam_id: this.examId }
				})
			])
				.then(function (results) {
					var allActive = self.normalizeListPayload(results[0].data);
					var fromClass = self.filterAllActiveForExam(allActive, cid, syid);
					var overtake = self.normalizeListPayload(results[1].data);
					self.eligibleRows = self.mergeOvertakeClassIntoEligible(fromClass, overtake);
					var regs = self.normalizeListPayload(results[2].data);
					self.registrationRecords = regs;
					var idMap = {};
					var baseline = [];
					var ri;
					for (ri = 0; ri < regs.length; ri++) {
						var rec = regs[ri];
						var sid =
							rec.student_id != null ? Number(rec.student_id) : rec.tbl_student && Number(rec.tbl_student.id);
						if (sid == null || isNaN(sid)) continue;
						if (rec.id != null) idMap[sid] = rec.id;
						baseline.push(sid);
					}
					self.registrationIdByStudentId = idMap;
					self.baselineRegisteredStudentIds = baseline.slice();
					self.selectedStudentIds = baseline.slice();
				})
				.catch(function () {
					self.studentsError =
						"Studentenlijst (all_active / inhaalles) of bestaande aanmeldingen konden niet worden geladen.";
				})
				.finally(function () {
					self.studentsLoading = false;
				});
		},
		submit() {
			var self = this;
			this.submitError = "";
			if (!this.examId || !this.hasChanges) return;
			if (this.needsUserForSave && this.userIdMissing) {
				this.submitError = "Geen geldige gebruiker (registered_by_user). Log opnieuw in om studenten toe te voegen.";
				return;
			}
			var baseline = new Set(
				this.baselineRegisteredStudentIds.map(function (x) {
					return Number(x);
				})
			);
			var selected = new Set(
				this.selectedStudentIds.map(function (x) {
					return Number(x);
				})
			);
			var toRemove = [];
			baseline.forEach(function (id) {
				if (!selected.has(id)) toRemove.push(id);
			});
			var toAdd = [];
			selected.forEach(function (id) {
				if (!baseline.has(id)) toAdd.push(id);
			});
			var regMap = this.registrationIdByStudentId;
			var deleteIds = [];
			var di;
			for (di = 0; di < toRemove.length; di++) {
				var rid = regMap[toRemove[di]];
				if (rid != null) deleteIds.push(rid);
			}
			this.saving = true;
			var deleteStep =
				deleteIds.length === 0
					? Promise.resolve()
					: Promise.all(
							deleteIds.map(function (rid) {
								return ax.post("/student_overtake_exam/delete/" + rid);
							})
					  );
			deleteStep
				.then(function () {
					if (toAdd.length === 0) return Promise.resolve(null);
					return ax.post("/student_overtake_exam/create_bulk", {
						exam_id: Number(self.examId),
						student_ids: toAdd,
						description:
							self.description && String(self.description).trim() !== ""
								? self.description
								: null,
						registered_by_user: self.loggedInUserId
					});
				})
				.then(function () {
					self.goBack();
				})
				.catch(function (err) {
					var msg =
						(err.response && err.response.data && err.response.data.msg) ||
						"Opslaan is mislukt. Controleer de invoer en probeer opnieuw.";
					self.submitError = msg;
					if (self.canSelectStudents) self.loadEligibleAndRegistered();
				})
				.finally(function () {
					self.saving = false;
				});
		}
	}
};
</script>

<style scoped>
code {
	padding: 0.1em 0.35em;
	background: rgba(0, 0, 0, 0.06);
	border-radius: 4px;
}
</style>
